import React, { useEffect, useState } from 'react';
import { X, FileText, MoveRight } from 'lucide-react';
import { FileManager } from '../FileManager';
import { useLanguage } from '../../i18n';
import { ModalLayer } from '../ui/Modal';

/**
 * Centered Documents modal — sits on top of the conversation rather than as
 * a split-screen panel. Two distinguishing behaviors vs. SettingsModal:
 *
 *   1. The backdrop is the PAI modal scrim (`bg-black/[0.58]` +
 *      `backdrop-blur-scrim`). This SUPERSEDES the original "no backdrop blur"
 *      product note: the design system makes every dialog opaque over one
 *      shared blurred scrim, and at 58% black there is nothing legible behind
 *      it to preserve anyway. The blur is safe here even though it creates a
 *      containing block for `fixed` descendants (DESIGN_SYSTEM rule 3) — this
 *      element is itself `fixed inset-0`, so an inner `fixed inset-0` dialog
 *      resolves to the identical rect.
 *   2. The backdrop doubles as a drop zone: when the user drags an item
 *      OUT of the FileManager, the area outside turns green with an
 *      "Add to conversation" hint. Releasing on that zone fires
 *      `omnis:document-ref-dropped` so MainOfficeView can attach the
 *      referenced file(s) to the next message — and closes the modal so
 *      the chat / freshly-added chips are immediately visible.
 *
 * The modal box itself never receives the drop — items dropped inside the
 * FileManager keep their original meaning (move into folders, etc.).
 */
const DocumentsModal = ({ isOpen, onClose }) => {
    const { t } = useLanguage();
    const [dragging, setDragging] = useState(null);

    useEffect(() => {
        if (!isOpen) return;
        const handleKey = (e) => {
            if (e.key === 'Escape') onClose();
        };
        const handleStart = (e) => setDragging(e.detail || true);
        const handleEnd = () => setDragging(null);

        document.addEventListener('keydown', handleKey);
        window.addEventListener('omnis:document-drag-start', handleStart);
        window.addEventListener('omnis:document-drag-end', handleEnd);
        return () => {
            document.removeEventListener('keydown', handleKey);
            window.removeEventListener('omnis:document-drag-start', handleStart);
            window.removeEventListener('omnis:document-drag-end', handleEnd);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const isDocDrag = !!dragging;

    const handleBackdropDragOver = (e) => {
        if (!isDocDrag) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    };

    const handleBackdropDrop = (e) => {
        if (!isDocDrag) return;
        e.preventDefault();
        const raw = e.dataTransfer.getData('application/x-document-ref');
        if (!raw) return;
        try {
            const payload = JSON.parse(raw);
            // Dispatch to MainOfficeView; close the modal so chips are visible.
            window.dispatchEvent(new CustomEvent('omnis:document-ref-dropped', { detail: payload }));
            setDragging(null);
            onClose();
        } catch (_) { /* ignore */ }
    };

    return (
        <div
            className={`fixed inset-0 z-[60] flex items-center justify-center p-2 md:p-6 backdrop-blur-scrim transition-colors ${
                isDocDrag
                    ? 'bg-pai-green/20'
                    : 'bg-black/[0.58]'
            }`}
            onClick={onClose}
            onDragOver={handleBackdropDragOver}
            onDrop={handleBackdropDrop}
        >
            {isDocDrag && (
                <div className="absolute top-8 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 rounded-full border border-pai-green/50 bg-pai-green/[0.12] text-pai-green text-sm font-semibold pointer-events-none animate-fade-slide-in">
                    <MoveRight size={16} />
                    {t('fmDropHereToAttach', 'Drop here to add to conversation')}
                </div>
            )}

            {/* `h-[85dvh]` below sizes the box preview-file-manager.spec.js (via the
                `file-manager-modal` testid) uses as the dialog box it then proves
                the preview escapes. */}
            <div
                data-testid="file-manager-modal"
                className="pai-dialog-frame bg-pai-modal border border-white/[0.13] rounded-modal shadow-modal w-full max-w-6xl h-[85dvh] overflow-hidden animate-fade-slide-in"
                onClick={(e) => e.stopPropagation()}
                // Boundary-aware DnD: any drop that lands INSIDE the modal box stays
                // internal. Folder moves are handled by the inner items/breadcrumb
                // (which stopPropagation); every other in-modal drop is a no-op.
                // Swallowing the event here stops it from bubbling to the backdrop's
                // "attach to conversation" handler, so reorganizing files never
                // triggers an accidental chat upload. Only drops on the backdrop
                // (outside the modal) attach to the conversation.
                onDragOver={(e) => { if (isDocDrag) { e.preventDefault(); e.stopPropagation(); } }}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); if (isDocDrag) setDragging(null); }}
            >
                {/* Header */}
                <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-pai-hairline flex-shrink-0">
                    <div className="flex items-center gap-3 min-w-0">
                        <FileText size={18} className="text-white flex-shrink-0" />
                        <h2 className="text-[17px] font-bold text-white flex-shrink-0">
                            {t('fmMyDocuments', 'My Documents')}
                        </h2>
                        <span className="text-[13px] text-pai-meta truncate">
                            {t('fmDragOutToAttachHint', 'Drag a file or folder out to attach it to your message')}
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        className="pai-icon-btn w-[38px] h-[38px] flex-shrink-0"
                        title={t('fmCloseEsc', 'Close (Esc)')}
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 min-h-0 overflow-hidden">
                    {/* The File Manager's own dialogs are portaled <Modal>s; this
                        tells them to layer ABOVE this z-[60] surface. */}
                    <ModalLayer z={60}>
                        <FileManager />
                    </ModalLayer>
                </div>
            </div>
        </div>
    );
};

export default DocumentsModal;
